import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MdAdd, MdEdit, MdDelete, MdPrint, MdDownload, MdGroups, MdQuestionAnswer } from "react-icons/md";
import dayjs from "dayjs";
import Card, { CardHeader } from "components/card";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import SideDrawer from "components/drawer";
import DataTable from "components/data-table";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { SegmentedControl } from "components/tabs";
import { InputField, Select, Switch, Textarea } from "components/fields";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { useAuth } from "hooks/useAuth";
import teamService from "services/teamService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { EMPTY, formatDate, formatDateTime } from "utils/format";
import { formatAnswer } from "./components/SubmissionCard";
import {
  ADMIN_ROLES,
  CheckInBadge,
  QUESTION_TYPES,
  WeekStepper,
  fullName,
  mondayOf,
  questionTypeLabel,
  roleLabel,
} from "./components/teamDisplay";

const SCOPE_OPTIONS = [
  { value: "direct_reports", label: "Direct Reports" },
  { value: "subtree", label: "Whole Team" },
];

const APPLIES_TO_OPTIONS = [
  { value: "direct_reports", label: "My direct reports only" },
  { value: "subtree", label: "Everyone below me" },
];

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const hourLabel = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${ampm}`;
};
const DAY_OPTIONS = DAY_NAMES.map((d, i) => ({ value: i, label: d }));
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: hourLabel(h) }));
const METHOD_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Email + SMS" },
];
const ASSESSMENT_OPTIONS = [
  { value: "never", label: "Off" },
  { value: "missing", label: "Auto (members without one)" },
];

const emptyForm = {
  prompt: "",
  questionType: "text",
  optionsText: "",
  appliesTo: "direct_reports",
  isActive: true,
};

const QuestionForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      prompt: form.prompt.trim(),
      questionType: form.questionType,
      appliesTo: form.appliesTo,
      isActive: form.isActive,
      options:
        form.questionType === "select"
          ? form.optionsText
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
    };
    const nextErrors = {};
    if (!payload.prompt) nextErrors.prompt = "Question text is required";
    if (form.questionType === "select" && (!payload.options || payload.options.length < 2)) {
      nextErrors.optionsText = "Multiple choice needs at least 2 comma-separated options";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSave(payload);
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-4 rounded-control border border-line bg-surface-raised p-4"
    >
      <Textarea
        label="Question"
        required
        rows={2}
        value={form.prompt}
        onChange={(e) => set("prompt", e.target.value)}
        error={errors.prompt}
        placeholder="e.g. What's one win from your ministry area this week?"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          label="Answer Type"
          value={form.questionType}
          onChange={(e) => set("questionType", e.target.value)}
          options={QUESTION_TYPES}
        />
        <Select
          label="Asked Of"
          value={form.appliesTo}
          onChange={(e) => set("appliesTo", e.target.value)}
          options={APPLIES_TO_OPTIONS}
        />
      </div>
      {form.questionType === "select" && (
        <InputField
          label="Options"
          required
          value={form.optionsText}
          onChange={(e) => set("optionsText", e.target.value)}
          error={errors.optionsText}
          hint="Comma separated, e.g. Great, Okay, Struggling"
        />
      )}
      <Switch
        label="Active"
        description="Inactive questions stay saved but aren't asked."
        checked={form.isActive}
        onChange={(e) => set("isActive", e.target.checked)}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={saving}>
          Save Question
        </Button>
      </div>
    </form>
  );
};

const AnswerValue = ({ answer }) => {
  if (answer.questionType === "scale" && typeof answer.answer !== "boolean") {
    const n = Number(answer.answer);
    const tone = n >= 4 ? "text-success-fg" : n <= 2 ? "text-danger-fg" : "text-warning-fg";
    return <span className={tone}>{answer.answer} / 5</span>;
  }
  return <span>{formatAnswer(answer.answer)}</span>;
};

const csvEscape = (v) => {
  let str = String(v ?? "");
  // Neutralize spreadsheet formula injection from member-entered answers
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  return `"${str.replace(/"/g, '""')}"`;
};
const htmlEscape = (v) =>
  String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const CheckIns = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionsError, setQuestionsError] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | question object
  const [saving, setSaving] = useState(false);

  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [scope, setScope] = useState("direct_reports");
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [sends, setSends] = useState([]);
  const [sendingNow, setSendingNow] = useState(null);

  useEffect(() => {
    teamService
      .getDeliverySettings()
      .then((res) => setDelivery(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    teamService
      .getDeliveryStatus({ weekStart })
      .then((res) => setSends(res.data || []))
      .catch(() => setSends([]));
  }, [weekStart]);

  const updateDelivery = async (patch) => {
    const prev = delivery;
    setDelivery({ ...delivery, ...patch });
    try {
      const res = await teamService.updateDeliverySettings(patch);
      setDelivery(res.data);
    } catch (error) {
      setDelivery(prev);
      notify.error(getApiErrorMessage(error, "Could not update schedule"));
    }
  };

  const handleSendNow = async (onlyPending) => {
    const confirmed = await confirm({
      title: onlyPending ? "Nudge Non-Responders?" : "Send Check-In Links?",
      message: onlyPending
        ? "Everyone who hasn't responded this week gets a reminder right now."
        : "Everyone on your team gets their check-in link right now.",
      confirmLabel: onlyPending ? "Send Nudges" : "Send Links",
    });
    if (!confirmed) return;
    try {
      setSendingNow(onlyPending ? "pending" : "all");
      const res = await teamService.sendNow({ onlyPending });
      const { sent, skipped, failed } = res.data || {};
      notify.success(`Sent ${sent ?? 0} · skipped ${skipped ?? 0}${failed ? ` · failed ${failed}` : ""}`);
      const deliveryStatus = await teamService.getDeliveryStatus({ weekStart });
      setSends(deliveryStatus.data || []);
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not send"));
    } finally {
      setSendingNow(null);
    }
  };

  const sendsByUser = useMemo(
    () =>
      sends.reduce((acc, s) => {
        if (s.status === "sent" && (!acc[s.userId] || s.sentAt > acc[s.userId].sentAt)) acc[s.userId] = s;
        return acc;
      }, {}),
    [sends]
  );
  const weekSendStats = sends.reduce(
    (acc, s) => ({ ...acc, [s.status]: (acc[s.status] || 0) + 1 }),
    {}
  );

  const fetchQuestions = useCallback(async () => {
    try {
      setLoadingQuestions(true);
      setQuestionsError(null);
      const res = await teamService.getQuestions({ includeInactive: "true" });
      setQuestions(res.data || []);
    } catch (error) {
      setQuestionsError(error);
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      setStatusError(null);
      const res = await teamService.getTeamStatus({ weekStart, scope });
      setStatus(res.data);
    } catch (error) {
      setStatusError(error);
    } finally {
      setLoadingStatus(false);
    }
  }, [weekStart, scope]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const saveQuestion = async (payload) => {
    try {
      setSaving(true);
      if (editing === "new") {
        await teamService.createQuestion(payload);
        notify.success("Question added");
      } else {
        await teamService.updateQuestion(editing.id, payload);
        notify.success("Question saved");
      }
      setEditing(null);
      fetchQuestions();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save question"));
    } finally {
      setSaving(false);
    }
  };

  const removeQuestion = async (q) => {
    const confirmed = await confirm({
      title: `Delete "${q.prompt}"?`,
      message: "Your team will stop seeing this question. Past answers are kept.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await teamService.deleteQuestion(q.id);
      notify.success("Question deleted");
      fetchQuestions();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete question"));
    }
  };

  const members = useMemo(() => (statusError ? [] : status?.members || []), [status, statusError]);
  const responded = status?.stats?.responded ?? 0;
  const total = status?.stats?.total ?? 0;

  const exportCSV = () => {
    if (!members.length) return;
    const rows = [
      ["Name", "Role", "Job Title", "Status", "Submitted", "Question", "Asked By", "Answer", "Notes"],
    ];
    members.forEach((m) => {
      const name = `${m.firstName} ${m.lastName}`;
      if (!m.submission) {
        rows.push([name, m.role, m.jobTitle || "", "No response", "", "", "", "", ""]);
        return;
      }
      const notes = (m.submission.notes || [])
        .map((n) => `${n.byName}: ${n.text}`)
        .join(" | ");
      (m.submission.answers || []).forEach((a, i) => {
        rows.push([
          name,
          m.role,
          m.jobTitle || "",
          "Responded",
          // Spreadsheet-friendly timestamp, not a display format
          dayjs(m.submission.submittedAt).format("YYYY-MM-DD HH:mm"),
          a.prompt,
          a.leaderName || "",
          String(a.answer),
          i === 0 ? notes : "",
        ]);
      });
    });
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `team-check-ins-${weekStart}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const printReport = () => {
    if (!members.length) return;
    const respondedMembers = members.filter((m) => m.submission);
    const pendingMembers = members.filter((m) => !m.submission);

    const memberBlock = (m) => `
      <div class="member">
        <div class="member-head">
          <strong>${htmlEscape(m.firstName)} ${htmlEscape(m.lastName)}</strong>
          <span class="muted">${htmlEscape(m.jobTitle || m.role)}</span>
          <span class="when">Submitted ${formatDateTime(m.submission.submittedAt)}</span>
        </div>
        ${(m.submission.answers || [])
          .map(
            (a) => `
          <div class="qa">
            <p class="q">${htmlEscape(a.prompt)}${a.leaderName ? ` <span class="muted">· asked by ${htmlEscape(a.leaderName)}</span>` : ""}</p>
            <p class="a">${htmlEscape(String(a.answer))}</p>
          </div>`
          )
          .join("")}
        ${
          (m.submission.notes || []).length
            ? `<div class="notes"><p class="notes-title">Notes</p>${m.submission.notes
                .map(
                  (n) =>
                    `<p class="note">${htmlEscape(n.text)} <span class="muted">· ${htmlEscape(n.byName)}, ${formatDate(n.at)}</span></p>`
                )
                .join("")}</div>`
            : ""
        }
      </div>`;

    // Standalone print document: fixed paper colors, independent of the app theme.
    const html = `<!DOCTYPE html>
<html><head><title>Team Check-In Report</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; color: #1a1a2e; margin: 40px; }
  h1 { font-size: 22px; margin: 0; }
  .sub { color: #555; font-size: 13px; margin: 6px 0 4px; font-family: Arial, sans-serif; }
  .summary { font-family: Arial, sans-serif; font-size: 13px; margin-bottom: 8px; }
  hr { border: none; border-top: 2px solid #1a1a2e; margin: 14px 0 20px; }
  .member { margin-bottom: 26px; page-break-inside: avoid; }
  .member-head { border-bottom: 1px solid #ddd; padding-bottom: 4px; margin-bottom: 8px; }
  .member-head strong { font-size: 15px; }
  .member-head .muted { margin-left: 8px; }
  .member-head .when { float: right; color: #777; font-size: 11px; font-family: Arial, sans-serif; }
  .qa { margin: 0 0 10px 12px; }
  .q { font-family: Arial, sans-serif; font-size: 11px; color: #666; margin: 0 0 2px; text-transform: uppercase; letter-spacing: 0.3px; }
  .a { font-size: 14px; margin: 0; }
  .muted { color: #999; font-weight: normal; font-size: 12px; text-transform: none; letter-spacing: 0; }
  .notes { margin: 8px 0 0 12px; background: #fdf6e3; padding: 8px 10px; border-left: 3px solid #e6c25a; }
  .notes-title { font-family: Arial, sans-serif; font-size: 10px; text-transform: uppercase; color: #997a1f; margin: 0 0 4px; }
  .note { font-size: 13px; margin: 0 0 4px; }
  .pending { font-family: Arial, sans-serif; font-size: 13px; color: #555; }
  .footer { margin-top: 30px; font-family: Arial, sans-serif; font-size: 10px; color: #aaa; }
</style></head>
<body>
  <h1>Weekly Team Check-In Report</h1>
  <p class="sub">${htmlEscape(user?.orgName || "")} · Week of ${formatDate(weekStart)} · ${scope === "subtree" ? "Whole team" : "Direct reports"}</p>
  <p class="summary"><strong>${responded} of ${total}</strong> team members responded</p>
  <hr/>
  ${respondedMembers.map(memberBlock).join("")}
  ${
    pendingMembers.length
      ? `<p class="pending"><strong>No response this week:</strong> ${pendingMembers
          .map((m) => htmlEscape(`${m.firstName} ${m.lastName}`))
          .join(", ")}</p>`
      : ""
  }
  <p class="footer">Generated by Atrium · ${formatDateTime(new Date())}</p>
<script>window.onload = function() { window.print(); };</script>
</body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      notify.error("Allow pop-ups to print the report");
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const columns = useMemo(
    () => [
      {
        id: "member",
        header: "Member",
        cell: ({ row }) => {
          const m = row.original;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar user={m} name={fullName(m)} size="sm" noBorder />
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-fg">
                  {fullName(m)}
                  {!m.isDirectReport && <Badge size="sm">Indirect</Badge>}
                </p>
                <p className="truncate text-xs text-fg-muted">{m.jobTitle || roleLabel(m.role)}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const m = row.original;
          const invite = !m.submission && sendsByUser[m.id];
          return (
            <div className="flex flex-col items-start gap-1">
              <CheckInBadge submitted={Boolean(m.submission)} />
              {invite && (
                <span
                  className="text-xs text-fg-muted"
                  title={`Last ${invite.kind} sent ${formatDateTime(invite.sentAt)} via ${invite.deliveryMethod}`}
                >
                  Invited {formatDate(invite.sentAt)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "submitted",
        header: "Submitted",
        cell: ({ row }) => (
          <span className="text-sm text-fg-muted">
            {row.original.submission ? formatDateTime(row.original.submission.submittedAt) : EMPTY}
          </span>
        ),
      },
    ],
    [sendsByUser]
  );

  let questionsContent;
  if (questionsError) {
    questionsContent = (
      <ErrorState
        message={getApiErrorMessage(questionsError, "We couldn't load your questions.")}
        onRetry={fetchQuestions}
      />
    );
  } else if (loadingQuestions) {
    questionsContent = <Skeleton lines={3} />;
  } else if (questions.length === 0 && editing === null) {
    questionsContent = (
      <EmptyState
        icon={MdQuestionAnswer}
        title="No Questions Yet"
        description="Add a question and everyone reporting to you will see it in their weekly check-in."
      />
    );
  } else {
    questionsContent = (
      <ul className="flex flex-col gap-2">
        {questions.map((q) => (
          <li
            key={q.id}
            className="flex items-center justify-between gap-3 rounded-control border border-line p-3"
          >
            <div className="min-w-0">
              <p className={`truncate text-sm font-semibold ${q.isActive ? "text-fg" : "text-fg-muted"}`}>
                {q.prompt}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
                {questionTypeLabel(q.questionType)}
                <span aria-hidden="true">·</span>
                {q.appliesTo === "subtree" ? "Everyone below me" : "Direct reports"}
                {!q.isActive && <Badge size="sm">Inactive</Badge>}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <IconButton
                icon={<MdEdit className="h-4 w-4" />}
                label="Edit question"
                size="sm"
                onClick={() => setEditing(q)}
              />
              <IconButton
                icon={<MdDelete className="h-4 w-4" />}
                label="Delete question"
                size="sm"
                onClick={() => removeQuestion(q)}
              />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Check-Ins"
        description="Ask your team custom questions each week and see who's responded. Questions cascade down your branch of the org chart."
        actions={<WeekStepper weekStart={weekStart} onChange={setWeekStart} />}
      />

      <Card padding="md" className="gap-4">
        <CardHeader
          title="My Questions"
          actions={
            editing === null && (
              <Button size="sm" leftIcon={<MdAdd className="h-4 w-4" />} onClick={() => setEditing("new")}>
                Add Question
              </Button>
            )
          }
        />

        {editing !== null && (
          <QuestionForm
            key={editing === "new" ? "new" : editing.id}
            initial={
              editing === "new"
                ? emptyForm
                : {
                    prompt: editing.prompt,
                    questionType: editing.questionType,
                    optionsText: (editing.options || []).join(", "),
                    appliesTo: editing.appliesTo,
                    isActive: editing.isActive,
                  }
            }
            onSave={saveQuestion}
            onCancel={() => setEditing(null)}
            saving={saving}
          />
        )}

        {questionsContent}
      </Card>

      {delivery && (
        <Card padding="md" className="gap-4">
          <CardHeader
            title="Delivery"
            description={
              <>
                {delivery.isEnabled
                  ? `Check-in links go out ${DAY_NAMES[delivery.sendDay]}s at ${hourLabel(delivery.sendHour)}${
                      delivery.reminderEnabled
                        ? `, reminder ${DAY_NAMES[delivery.reminderDay]}s at ${hourLabel(delivery.reminderHour)}`
                        : ""
                    } via ${delivery.deliveryMethod === "both" ? "email + SMS" : delivery.deliveryMethod}. No login needed: links open a one-tap check-in page.`
                  : "Automatic sending is turned off."}
                {sends.length > 0 && (
                  <span className="mt-1 block">
                    This week: {weekSendStats.sent || 0} delivered
                    {weekSendStats.skipped ? ` · ${weekSendStats.skipped} skipped` : ""}
                    {weekSendStats.failed ? ` · ${weekSendStats.failed} failed` : ""}
                  </span>
                )}
              </>
            }
            actions={
              isAdmin && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={sendingNow === "pending"}
                    disabled={!!sendingNow}
                    onClick={() => handleSendNow(true)}
                  >
                    Nudge Non-Responders
                  </Button>
                  <Button
                    size="sm"
                    loading={sendingNow === "all"}
                    disabled={!!sendingNow}
                    onClick={() => handleSendNow(false)}
                  >
                    Send Links Now
                  </Button>
                </>
              )
            }
          />

          {isAdmin && (
            <div className="flex flex-col gap-4 border-t border-line pt-4">
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <Switch
                  label="Automatic Sending"
                  checked={delivery.isEnabled}
                  onChange={(e) => updateDelivery({ isEnabled: e.target.checked })}
                />
                <Switch
                  label="Reminder"
                  checked={delivery.reminderEnabled}
                  onChange={(e) => updateDelivery({ reminderEnabled: e.target.checked })}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <Select
                  label="Send Day"
                  value={delivery.sendDay}
                  onChange={(e) => updateDelivery({ sendDay: Number(e.target.value) })}
                  options={DAY_OPTIONS}
                />
                <Select
                  label="Send Time"
                  value={delivery.sendHour}
                  onChange={(e) => updateDelivery({ sendHour: Number(e.target.value) })}
                  options={HOUR_OPTIONS}
                />
                <Select
                  label="Delivery Method"
                  value={delivery.deliveryMethod}
                  onChange={(e) => updateDelivery({ deliveryMethod: e.target.value })}
                  options={METHOD_OPTIONS}
                />
                {delivery.reminderEnabled && (
                  <>
                    <Select
                      label="Reminder Day"
                      value={delivery.reminderDay}
                      onChange={(e) => updateDelivery({ reminderDay: Number(e.target.value) })}
                      options={DAY_OPTIONS}
                    />
                    <Select
                      label="Reminder Time"
                      value={delivery.reminderHour}
                      onChange={(e) => updateDelivery({ reminderHour: Number(e.target.value) })}
                      options={HOUR_OPTIONS}
                    />
                  </>
                )}
                <Select
                  label="Assessment Invites"
                  hint="Include an assessment invite with the weekly check-in for members who haven't completed one."
                  value={delivery.includeAssessment || "never"}
                  onChange={(e) => updateDelivery({ includeAssessment: e.target.value })}
                  options={ASSESSMENT_OPTIONS}
                />
              </div>
            </div>
          )}
        </Card>
      )}

      <Card padding="md" className="gap-4">
        <CardHeader
          title="Weekly Responses"
          description={total ? `${responded} of ${total} responded` : "No team members in scope"}
          actions={
            <>
              <SegmentedControl
                aria-label="Team scope"
                value={scope}
                onChange={setScope}
                options={SCOPE_OPTIONS}
              />
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<MdPrint className="h-4 w-4" />}
                disabled={!members.length}
                title="Print-ready weekly report"
                onClick={printReport}
              >
                Print
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<MdDownload className="h-4 w-4" />}
                disabled={!members.length}
                title="Download responses as CSV"
                onClick={exportCSV}
              >
                CSV
              </Button>
            </>
          }
        />

        {total > 0 && !statusError && (
          <div
            role="progressbar"
            aria-label="Responses received"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={responded}
            className="h-2 w-full overflow-hidden rounded-full bg-surface-subtle"
          >
            <div
              className="h-full rounded-full bg-success transition-all"
              style={{ width: `${(responded / total) * 100}%` }}
            />
          </div>
        )}

        {statusError ? (
          <ErrorState
            message={getApiErrorMessage(statusError, "We couldn't load team responses.")}
            onRetry={fetchStatus}
          />
        ) : (
          <DataTable
            variant="plain"
            aria-label="Weekly responses"
            columns={columns}
            data={members}
            loading={loadingStatus}
            getRowId={(m) => String(m.id)}
            onRowClick={(m) => setSelectedMember(m)}
            emptyState={
              <EmptyState
                icon={MdGroups}
                title="No One Reports to You Yet"
                description="Ask an admin to assign team members to you on the Org Chart page."
              />
            }
          />
        )}
      </Card>

      <SideDrawer
        isOpen={Boolean(selectedMember)}
        onClose={() => setSelectedMember(null)}
        title={selectedMember ? fullName(selectedMember) : ""}
        subtitle={`Week of ${formatDate(weekStart)}`}
      >
        {selectedMember && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <CheckInBadge submitted={Boolean(selectedMember.submission)} size="md" />
              {selectedMember.submission && (
                <span className="text-sm text-fg-muted">
                  Submitted {formatDateTime(selectedMember.submission.submittedAt)}
                </span>
              )}
            </div>
            {selectedMember.submission ? (
              <dl className="flex flex-col gap-4">
                {(selectedMember.submission.answers || []).map((a, i) => (
                  <div key={i}>
                    <dt className="text-xs text-fg-muted">
                      {a.prompt}
                      {a.leaderName ? <span> · asked by {a.leaderName}</span> : null}
                    </dt>
                    <dd className="mt-0.5 text-sm font-medium text-fg">
                      <AnswerValue answer={a} />
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <EmptyState
                variant="inline"
                title={
                  sendsByUser[selectedMember.id]
                    ? `No response yet. Their check-in link went out ${formatDateTime(
                        sendsByUser[selectedMember.id].sentAt
                      )}.`
                    : "No response yet this week."
                }
              />
            )}
          </div>
        )}
      </SideDrawer>
    </PageContainer>
  );
};

export default CheckIns;
